import { Controller } from '@hotwired/stimulus'

// Importe de ejemplo del campo (el `placeholder`, que solo se ve si se vacía).
// Va MIL para no contradecir al valor con el que el servidor sirve el campo: dos
// cifras distintas en el mismo hueco se leen como que una de las dos está mal.
//
// Consecuencia asumida, y es solo del placeholder: el español pone
// `minimumGroupingDigits: 2`, así que en rankia.com el ejemplo sale «1000», sin
// punto de millares. En los otros seis dominios (mx, ar, cl, pe, co, us) sí se
// agrupa. Forzarlo con `useGrouping: 'always'` no vale: es de ES2023 y depende
// de la versión de ICU del Node que lo ejecute, así que el resultado cambiaba
// entre mi máquina y CI.
const EJEMPLO_DE_IMPORTE = 1000

// Simulador de inversión de la ficha de acción (HU-12 de la Entrega 2).
//
// Responde a una sola pregunta: con este dinero, ¿cuántas acciones compro?
// El cálculo se actualiza al teclear y usa ACCIONES COMPLETAS, que es lo que se
// puede comprar de verdad; el resto queda como efectivo no invertido y se dice
// en voz alta en vez de redondearse a favor.
//
// Todo lo que necesita saber viaja en valores del elemento: el precio, la
// divisa y la rentabilidad por dividendo las resuelve el servidor con la misma
// cadena de fiabilidad que el resto de la ficha. Aquí no se pide nada por red.
//
// Va en el manifiesto global de controladores y no en un pack propio, al
// contrario que `simulador-pension`: aquel se sacó por tamaño (85 KB) y este no
// llega a 2 KB ni arrastra dependencias. La explicación vive aquí y no junto a
// su línea del manifiesto porque ese fichero lo regenera
// `bin/rails stimulus:manifest:update` y se lleva por delante los comentarios.
export default class extends Controller {
  static targets = ['importe', 'acciones', 'invertido', 'efectivo', 'dividendo', 'dividendoBloque', 'resultado']
  static values = {
    precio: Number,
    moneda: { type: String, default: '' },
    rentabilidadDividendo: { type: Number, default: 0 },
    locale: { type: String, default: 'es-ES' }
  }

  connect () {
    this.ponerEjemploEnElCampo()
    this.calcular()
  }

  // El ejemplo del campo lo escribe el MISMO `Intl` que lee lo que se teclea. Con
  // un `placeholder` fijo en la plantilla, en rankia.mx el campo proponía «1.000»
  // y el parser de ese dominio leía ese punto como decimal: la página enseñaba un
  // ejemplo que ella misma interpretaba como uno.
  //
  // No se escribe si el marcado ya trae uno propio.
  ponerEjemploEnElCampo () {
    if (this.importeTarget.placeholder) return

    this.importeTarget.placeholder = this.numero(EJEMPLO_DE_IMPORTE, 0)
  }

  calcular () {
    const importe = this.importeIntroducido

    // Sin importe todavía no hay nada que contar: el bloque de resultados se
    // esconde en vez de enseñar tres ceros, que se leen como un resultado.
    if (!(importe > 0) || !(this.precioValue > 0)) {
      this.mostrarResultados(false)
      return
    }
    this.mostrarResultados(true)

    const acciones = this.accionesQueCaben(importe)
    const invertido = acciones * this.precioValue

    this.accionesTarget.textContent = this.numero(acciones, 0)
    this.invertidoTarget.textContent = this.importe(invertido)
    // `Math.max(0, …)`: en coma flotante, `acciones * precio` puede quedar un
    // épsilon POR ENCIMA del importe (3 × 0,1 da 0,30000000000000004), y el
    // efectivo salía en negativo y se pintaba «-0,00 €».
    this.efectivoTarget.textContent = this.importe(Math.max(0, importe - invertido))

    if (this.hasDividendoTarget) {
      this.dividendoTarget.textContent = this.importe(invertido * this.rentabilidadDividendoValue / 100)
    }
  }

  // Cuántas acciones completas caben en el importe.
  //
  // El `toFixed` antes del `floor` NO es cosmético: en coma flotante binaria,
  // `0.29 / 0.01` da 28.999999999999996 y el suelo son 28 acciones en vez de 29.
  // Rankia lista valores por debajo del céntimo (Nyesa cotiza a 0,004), así que
  // este caso no es de laboratorio. Se redondea a ocho decimales, muy por encima
  // de cualquier precisión de precio real, y luego se baja al entero.
  accionesQueCaben (importe) {
    return Math.floor(Number((importe / this.precioValue).toFixed(8)))
  }

  // El dividendo estimado vive en la otra tarjeta pero cuelga del mismo importe,
  // así que aparece y desaparece con los resultados. Dejarlo visible con la cifra
  // en blanco enseña una etiqueta sin dato, que se lee como que falta algo.
  mostrarResultados (visible) {
    this.resultadoTarget.hidden = !visible
    if (this.hasDividendoBloqueTarget) this.dividendoBloqueTarget.hidden = !visible
  }

  // Los separadores no se dan por supuestos: se le preguntan al MISMO locale con
  // el que se formatean los resultados. En España el decimal es la coma y el de
  // millares el punto, pero en México, Perú y Estados Unidos es justo al revés,
  // y con la pareja fija de España un «1000.50» tecleado en rankia.mx se leía
  // como cien mil cincuenta.
  get separadores () {
    const partes = new Intl.NumberFormat(this.localeValue).formatToParts(12345.6)

    return {
      decimal: partes.find(parte => parte.type === 'decimal')?.value || ',',
      grupo: partes.find(parte => parte.type === 'group')?.value || '.'
    }
  }

  // El campo es de texto libre, así que llega lo que el usuario escriba: se
  // descarta el separador de millares, que si no convertiría "1.000" en 1, y se
  // normaliza el decimal. `split`/`join` en vez de expresión regular porque el
  // separador de grupo puede ser un espacio duro, que habría que escapar.
  get importeIntroducido () {
    const { decimal, grupo } = this.separadores
    const crudo = this.importeTarget.value.trim().split(grupo).join('').replace(decimal, '.')
    const numero = Number.parseFloat(crudo)

    return Number.isFinite(numero) ? numero : 0
  }

  importe (valor) {
    if (!this.monedaValue) return this.numero(valor, 2)

    return new Intl.NumberFormat(this.localeValue, {
      style: 'currency', currency: this.monedaValue, maximumFractionDigits: 2
    }).format(valor)
  }

  numero (valor, decimales) {
    return new Intl.NumberFormat(this.localeValue, {
      minimumFractionDigits: decimales, maximumFractionDigits: decimales
    }).format(valor)
  }
}
